import React, { JSX, useEffect, useState } from "react";
import { Box, CircularProgress, Paper, Typography, alpha, useTheme } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IField } from "../../types/table/interfaces.type";
import CustomCheckbox from "../CustomCheckbox";
import { StatusBadge, SyncTableCellValue } from "./DataTableBody";

import { isFromControl } from "./DataTableBody";

interface DataCardListProps<T> {
  fields: IField<T>[];
  content: T[];
  renderActions?: (row: T) => React.ReactElement[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  onRowClick?: (row: T) => void;
}

function PromisedValue({ promise }: { promise: Promise<any> }) {
  const [element, setElement] = useState<JSX.Element>(
    <CircularProgress size={16} thickness={4} />
  );
  useEffect(() => {
    let mounted = true;
    promise
      .then((value) => mounted && setElement(<SyncTableCellValue value={value} />))
      .catch(() => mounted && setElement(<WarningAmberIcon color="error" fontSize="small" />));
    return () => {
      mounted = false;
    };
  }, [promise]);
  return element;
}

function FieldValue<T>({ row, field }: { row: T; field: IField<T> }) {
  if (field.render) return <>{field.render(row)}</>;
  if (field.transform) return <PromisedValue promise={Promise.resolve(field.transform(row))} />;
  if (field.key === "status") return <StatusBadge value={row[field.key]} />;
  return <SyncTableCellValue value={row[field.key]} />;
}

/**
 * The phone layout of PaginatedTable: one card per row instead of a table
 * that scrolls sideways. The first column becomes the card's title, the rest
 * are label/value lines, and the row's actions sit along the bottom.
 */
function DataCardList<T>({
  fields,
  content,
  renderActions,
  shadeRow,
  shadeBg,
  checkboxSelect,
  onRowSelect,
  isRowSelected,
  onRowClick,
}: DataCardListProps<T>) {
  const theme = useTheme();

  if (content.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, py: 5 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            mb: 0.5,
          }}
        >
          <InboxOutlinedIcon />
        </Box>
        <Typography sx={{ fontWeight: 600 }}>No data available</Typography>
        <Typography variant="body2" color="text.secondary">
          Nothing to show here yet.
        </Typography>
      </Box>
    );
  }

  const [titleField, ...detailFields] = fields;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 1.25 }}>
      {content.map((row, rowIndex) => {
        const rowKey =
          (row as any)?.id ?? (row as any)?._id ?? (row as any)?.key ?? rowIndex;
        const shaded = shadeRow ? shadeRow(row) : false;
        const shade = shaded
          ? typeof shadeBg === "function"
            ? shadeBg(row)
            : shadeBg ?? "#FFEEAA"
          : undefined;
        const actions = renderActions ? renderActions(row) : [];

        return (
          <Paper
            key={String(rowKey)}
            elevation={0}
            onClick={onRowClick ? (e: React.MouseEvent) => { if (!isFromControl(e)) onRowClick(row); } : undefined}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
              px: 1.75,
              py: 1.5,
              bgcolor: shade ?? "background.paper",
              ...(onRowClick ? { cursor: "pointer" } : {}),
            }}
          >
            {titleField && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: detailFields.length ? 1 : 0 }}>
                {checkboxSelect && (
                  <CustomCheckbox
                    checked={isRowSelected ? isRowSelected(row) : false}
                    onChange={(e) => onRowSelect && onRowSelect(row, e.target.checked)}
                    ariaLabel="Select row"
                  />
                )}
                <Box
                  sx={{
                    minWidth: 0,
                    flexGrow: 1,
                    "& .MuiTypography-root": {
                      fontWeight: 700,
                      fontSize: "1rem",
                      textAlign: "left",
                      justifyContent: "flex-start",
                      whiteSpace: "normal",
                    },
                  }}
                >
                  <FieldValue row={row} field={titleField} />
                </Box>
              </Box>
            )}

            {detailFields.map((field) => (
              <Box
                key={String(field.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 0.5,
                  minHeight: 32,
                }}
              >
                <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", flexShrink: 0 }}>
                  {field.header}
                </Typography>
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                    // The table's renderers centre their text; on a card the
                    // value hugs the right edge and may wrap.
                    "& .MuiTypography-root": {
                      textAlign: "right",
                      justifyContent: "flex-end",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    },
                    "& > *": { flex: "0 1 auto" },
                  }}
                >
                  <FieldValue row={row} field={field} />
                </Box>
              </Box>
            ))}

            {actions.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  gap: 0.5,
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  // Phone-sized hit targets for the icon buttons.
                  "& .MuiButton-root, & .MuiIconButton-root": {
                    minWidth: 40,
                    minHeight: 40,
                    p: 1,
                  },
                }}
              >
                {actions}
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

export default DataCardList;
